import ExclamationTriangle from "@/components/core/badges/ExclamationTriangle";
import { requestGetAttestation } from "@/state/document/DocumentEffects";
import { TransactionDetail } from "@/type/core/transaction/transaction.types";
import { FileHelper } from "@/utils/helpers/file-helper";
import { TransactionHelper } from "@/utils/helpers/transaction-helper";
import DuplicateButton from "@components/core/buttons/DuplicateButton";
import PageLayout from "@components/core/layouts/PageLayout";
import BasicLink from "@components/core/links/BasicLink";
import InfoModal from "@components/core/modals/InfoModal";
import { ROUTES } from "@routes/Routes";
import { authenticationStore } from "@state/auth/AuthStore";
import { degradedStore } from "@state/degraded/DegradedStore";
import { DegradedModeDTO } from "@state/degraded/dto/response/degraded.mode.dto";
import { requestSearchMembersForOffice } from "@state/offices/OfficesEffects";
import { requestGetTransactionDetail } from "@state/transactions/TransactionEffects";
import DownloadIcon from "@static/core/icons/icon-download.svg?react";
import SignatureIcon from "@static/core/icons/icon-signature.svg?react";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import {
  Contact,
  Document,
  History,
  Signatory,
} from "@type/core/dashboard/dashboard.types";
import { Office } from "@type/core/offices/offices.types";
import { getToken } from "@utils/helpers/api.helper";
import { formatOfficeName } from "@utils/helpers/format-helper";
import { toastError } from "@utils/helpers/toast-helper";
import TransactionButtonsSection from "@views/core/extranet/transactions/details/sections/TransactionButtonsSection";
import TransactionConfidentialitySection from "@views/core/extranet/transactions/details/sections/TransactionConfidentialitySection";
import TransactionDocumentsSection from "@views/core/extranet/transactions/details/sections/TransactionDocumentsSection";
import TransactionMailsSection from "@views/core/extranet/transactions/details/sections/TransactionMailsSection";
import TransactionSignatoriesSection from "@views/core/extranet/transactions/details/sections/TransactionSignatoriesSection";
import TransactionTitleSection from "@views/core/extranet/transactions/details/sections/TransactionTitleSection";
import { useUnit } from "effector-react";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const TransactionScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const transactionId = useParams().transactionId;
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const degradedModeStore = useUnit<DegradedModeDTO | null>(degradedStore);

  const [loading, setLoading] = useState<boolean>(true);
  const [transaction, setTransaction] = useState<TransactionDetail>();
  const [signatories, setSignatories] = useState<Signatory[]>([]);
  const [office, setOffice] = useState<Office>();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [attachments, setAttachments] = useState<Document[]>([]);
  const [histories, setHistories] = useState<History[]>([]);
  const [authorizedExperts, setAuthorizedExperts] = useState<Contact[]>([]);
  const [attestationLinkClicked, setAttestationLinkClicked] =
    useState<boolean>(false);
  const [hasDataOrigin, setHasDataOrigin] = useState<boolean>(false);
  const [members, setMembers] = useState<Contact[]>([]);

  const user = authStore?.user;

  const certificateDegradedMode: boolean =
    degradedModeStore?.certificateDegradedMode ?? false;

  const signatureDegradedMode: boolean =
    degradedModeStore?.signatureDegradedMode ?? false;

  const sealDegradedMode: boolean =
    degradedModeStore?.sealDegradedMode ?? false;

  const isAuthorized =
    transaction &&
    (!transaction.confidential ||
      authorizedExperts.find((expert) => user && expert.email === user.email));

  const showDownloadAll =
    isAuthorized &&
    transaction &&
    documents &&
    attachments &&
    documents.length + attachments.length > 1 &&
    (documents.filter((document) => document.fileSize).length > 0 ||
      attachments.filter((attachment) => attachment.fileSize).length > 0);

  const showDownloadAttestation =
    isAuthorized &&
    transaction &&
    transaction.type === "signature" &&
    transaction.universignVersion === "U2" &&
    transaction.status === "finished";

  const showDownloadFilesNotSigned =
    isAuthorized &&
    transaction &&
    transaction.universignVersion === "U2" &&
    transaction.status !== "finished";

  const totalNumberSignatories = signatories.length;
  const signedNumberSignatories = signatories.filter(
    (signatory) => signatory.status === "signed",
  ).length;
  const canTransactionBeFinalized =
    signedNumberSignatories !== totalNumberSignatories;

  useEffect(() => {
    if (transactionId) {
      setLoading(true);
      void requestGetTransactionDetail({ id: transactionId });
    }
  }, [transactionId]);

  useEffect(() => {
    return requestGetTransactionDetail.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setTransaction(result.data.transaction);
        setSignatories(result.data.signatories);
        setOffice(result.data.office);
        setDocuments(
          result.data.documents.sort((a, b) =>
            TransactionHelper.documentSorter(a, b),
          ),
        );
        setAttachments(result.data.attachments);
        setHistories(result.data.histories);
        setAuthorizedExperts(result.data.authorizedExperts);
        setHasDataOrigin(result.data.hasDataOrigin);
      } else {
        toastError(t("error.transactionDetail.retrieve"));
      }
      setLoading(false);
    });
  });

  useEffect(() => {
    if (office) {
      void requestSearchMembersForOffice({ id: office.id });
    }
  }, [office]);

  useEffect(() => {
    return requestSearchMembersForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setMembers(result.data.contacts || []);
      } else {
        toastError(t("error.retrieveMembers"));
      }
    });
  });

  useEffect(() => {
    if (
      transaction &&
      transaction.type === "scellement" &&
      transaction.status === "inProgress"
    ) {
      navigate(ROUTES.extranet.transactions.process.generate(transaction.id));
    }
  }, [transaction, navigate]);

  const handleDownloadAttestation = () => {
    if (!transactionId) {
      return;
    }

    setAttestationLinkClicked(true);
    requestGetAttestation({ dto: { token: getToken() }, id: transactionId })
      .then((response) => {
        if (response.ok && response.data) {
          const filename = `attestation_transaction_${transactionId}.pdf`;
          FileHelper.createDownloadFileFromBlob(
            response.data,
            filename,
            response.headers,
          );
        } else {
          toastError(t("error.toast.retrieveDocuments"));
        }
      })
      .catch(() => toastError(t("error.toast.retrieveDocuments")))
      .finally(() => setAttestationLinkClicked(false));
  };

  return (
    <PageLayout
      container
      returnRoute={ROUTES.extranet.home.generate()}
      contentInCard
      isLoading={loading}
    >
      {transaction && office ? (
        <>
          <div className="d-flex align-items-center justify-content-end flex-wrap gap-16 mb-24">
            <h2 className="flex-grow-1 text-primary font-weight-bold mb-0">
              {`Collecte de ${t(
                transaction && transaction.type + ".label",
              ).toLowerCase()} du ${formatOfficeName(office && office.alias)}`}
            </h2>
            {isAuthorized && (
              <DuplicateButton
                shouldShowInfo={true}
                hasDataOrigin={hasDataOrigin}
                transaction={transaction}
                office={office}
              />
            )}
          </div>
          <TransactionTitleSection
            transaction={transaction}
            histories={histories}
          />
          <div className="border-bottom-dark-grey">
            {(showDownloadAll || showDownloadAttestation) && (
              <div className="p-24">
                <div className="d-flex align-items-center">
                  {showDownloadAll && (
                    <h2 className="d-flex align-items-center text-black flex-grow-1 mb-0">
                      <span>
                        {showDownloadFilesNotSigned
                          ? t("transaction.detail.downloadAllFilesNotSigned")
                          : t("transaction.detail.downloadAllFiles")}{" "}
                        :
                      </span>
                      <BasicLink
                        to={`/documents/${transaction.id}?token=${getToken()}&filename=${encodeURIComponent(transaction.object ?? "")}`}
                        title={t("transaction.detail.downloadAllFiles")}
                        className="d-flex align-items-center"
                        forceBrowserRedirect
                      >
                        <DownloadIcon className="text-black icon-30" />
                      </BasicLink>
                    </h2>
                  )}
                  {showDownloadAttestation && (
                    <div className="d-flex align-items-center">
                      <div
                        onClick={handleDownloadAttestation}
                        className="d-inline-flex align-items-center justify-content-center position-relative gap-8 app-link-btn text-decoration-none btn-transparent"
                      >
                        <SignatureIcon className="text-black icon-30" />
                        <span className="text text-ellipsis">
                          {attestationLinkClicked
                            ? t("transaction.detail.attestation.downloading")
                            : t("transaction.detail.attestation.download")}
                        </span>
                      </div>
                      <InfoModal value="attestation" info />
                    </div>
                  )}
                </div>
              </div>
            )}
            <TransactionDocumentsSection
              title={t("transaction.detail.documents")}
              authorized={!!isAuthorized}
              documents={documents}
              showDownload={!showDownloadAll}
              transaction={transaction}
            />
            <TransactionConfidentialitySection
              office={office}
              transactionId={transactionId}
              transaction={transaction}
              authorizedExperts={authorizedExperts}
              isAuthorized={!!isAuthorized}
              members={members}
              user={user}
              setLoading={setLoading}
            />
            {attachments && attachments.length > 0 && (
              <TransactionDocumentsSection
                title={t("transaction.detail.attachments")}
                authorized={!!isAuthorized}
                documents={attachments}
                showDownload={!showDownloadAll}
                isAttachmentsPanel
                transaction={transaction}
              />
            )}
          </div>
          {transaction && transaction.type === "signature" && (
            <TransactionSignatoriesSection
              user={user}
              officeRights={office.rights ?? []}
              transactionId={transactionId}
              transaction={transaction}
              signatories={signatories}
              documents={documents}
              authorized={!!isAuthorized}
              signatureDegradedMode={signatureDegradedMode}
              className="border-bottom-dark-grey"
            />
          )}
          <TransactionMailsSection transaction={transaction} />
          <TransactionButtonsSection
            user={user}
            transactionId={transactionId}
            transaction={transaction}
            signatories={signatories}
            documents={documents}
            certificateDegradedMode={certificateDegradedMode}
            signatureDegradedMode={signatureDegradedMode}
            sealDegradedMode={signatureDegradedMode}
            setLoading={setLoading}
            officeRights={office ? office.rights : []}
            canTransactionBeFinalized={canTransactionBeFinalized}
          />
          {(certificateDegradedMode || signatureDegradedMode) &&
            transaction &&
            transaction.type === "signature" &&
            !transaction.classified &&
            transaction.status !== "finished" &&
            transaction.status !== "canceled" && (
              <ExclamationTriangle
                text={t(
                  certificateDegradedMode && signatureDegradedMode
                    ? "transaction.detail.detailCertificateAndSignatureServiceUnavailable"
                    : certificateDegradedMode
                      ? "transaction.detail.detailCertificateServiceUnavailable"
                      : "transaction.detail.detailSignatureServiceUnavailable",
                )}
              />
            )}
          {sealDegradedMode &&
            transaction &&
            transaction.type === "scellement" &&
            !transaction.classified &&
            transaction.status !== "finished" &&
            transaction.status !== "canceled" && (
              <ExclamationTriangle
                text={t("transaction.detail.detailSealServiceUnavailable")}
              />
            )}
          {/* TODO: MODAL -> orderModal ? */}
        </>
      ) : (
        <></>
      )}
    </PageLayout>
  );
};

export default TransactionScreen;
